/**
 * @typedef {object} PolygonStyle
 * @property {boolean} fill 是否填充
 * @property {Cesium.Color} color 填充颜色
 * @property {boolean} outline 是否描边
 * @property {Cesium.Color} outlineColor 描边颜色
 * @property {number} outlineWidth 描边宽度
 * @property {boolean} isExtrudedHeight 是否从0开始拉高面
 * @property {boolean} useFlowWaterMaterial 是否使用流动水材质
 */
/**
 * @typedef {object} VectorPrimitiveOptions
 * @property {string} id 唯一标识
 * @property {string} name 名称
 * @property {Cesium.DataSource} dataSource
 * @property {PolygonStyle?} polygonStyle 多边形样式
 */
const { FeatureBase, geoMath, coordTransform } = map3dC;

const defaultLabelStyle = {
  font: '600 14px Helvetica Neue,Helvetica,Arial,Microsoft Yahei,Hiragino Sans GB,Heiti SC,WenQuanYi Micro Hei,sans-serif',
  fillColor: Cesium.Color.WHITE,
  outlineColor: Cesium.Color.BLACK,
  outlineWidth: 1,
  horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
  style: Cesium.LabelStyle.FILL_AND_OUTLINE,
  showBackground: false,
  disableDepthTestDistance: Number.POSITIVE_INFINITY,
  scaleByDistance: new Cesium.NearFarScalar(10000, 1, 50000, 0.1),
};

class VectorPrimitive extends FeatureBase {
  /**
   * @type {Cesium.DataSource}
   */
  _dataSource;
  /**
   * @type {Cesium.PrimitiveCollection}
   */
  _collection;
  /**
   * @type {Cesium.PointPrimitiveCollection?}
   */
  _pointCollection;
  /**
   * @type {Cesium.GroundPolylinePrimitive?}
   */
  _polyline;
  /**
   * @type {Cesium.Primitive|GroundPrimitive|undefined}
   */
  _polygon;
  /**
   * @type {Cesium.Primitive|GroundPolylinePrimitive|undefined}
   */
  _polygonOutline;
  /**
   * @type {Cesium.LabelCollection?}
   */
  _labelCollection;
  /**
   * @type {PolygonStyle?}
   */
  _polygonStyle;

  /**
   * 构造函数
   * @param {VectorPrimitiveOptions} options
   */
  constructor(options) {
    options = options ?? {};
    super(options);
    if (!options.dataSource) {
      throw new Error('dataSource是必选参数');
    }
    this._dataSource = options.dataSource;

    this._polygonStyle = options.polygonStyle;

    this._collection = new Cesium.PrimitiveCollection();

    this._resolveDataSource();
  }

  /**
   * 添加进地图
   * @param {Cesium.Viewer} viewer
   */
  addToViewer(viewer) {
    if (!this._viewer) {
      this._viewer = viewer;

      this._viewer.scene.primitives.add(this._collection);
    }
  }

  /**
   * 从地图中移除，默认不会销毁
   * @param {boolean} destroy 是否销毁
   */
  removeFromViewer(destroy = false) {
    if (this._viewer && !this._viewer.isDestroyed()) {
      const bakDestroy = this._viewer.scene.primitives.destroyPrimitives;
      this._viewer.scene.primitives.destroyPrimitives = destroy;

      this._viewer.scene.primitives.remove(this._collection);

      this._viewer.scene.primitives.destroyPrimitives = bakDestroy;
    }
    this._viewer = null;
  }

  /**
   * 定位
   * @param {object} options
   * @param {number} options.duration
   * @param {Cesium.HeadingPitchRange} options.offset
   */
  flyTo(options) {
    if (this._viewer) {
      options = options ?? {};
      options.duration = options.duration ?? 1;
      options.offset =
        options.offset ?? new Cesium.HeadingPitchRange(0, Cesium.Math.toRadians(-90), 0);

      const removeListener = this._viewer.scene.postRender.addEventListener(() => {
        const scratchBoundingSphere = new Cesium.BoundingSphere();
        let unionBoundingSphere;
        if (this._pointCollection?.length >= 1) {
          const positions = [];
          for (let i = 0; i < this._pointCollection.length; i++) {
            const point = this._pointCollection.get(i);
            positions.push(point.position);
          }
          unionBoundingSphere = Cesium.BoundingSphere.fromPoints(positions);
        }
        if (this._polyline?.ready) {
          let positions = [];
          const geometryInstances = this._polyline._primitiveOptions.geometryInstances;
          geometryInstances.forEach((instance) => {
            positions = positions.concat(instance.geometry._positions);
          });
          const boundingSphere = Cesium.BoundingSphere.fromPoints(positions);
          if (!unionBoundingSphere) {
            unionBoundingSphere = boundingSphere;
          } else {
            unionBoundingSphere = Cesium.BoundingSphere.union(
              unionBoundingSphere,
              boundingSphere,
              scratchBoundingSphere
            );
          }
        }
        if (this._polygon?.ready) {
          const boundingSpheres = this._polygon._boundingSpheres ?? [];
          if (boundingSpheres.length >= 1) {
            let startIndex = 0;
            if (!unionBoundingSphere) {
              unionBoundingSphere = boundingSpheres[0];
              startIndex = 1;
            }
            for (let i = startIndex; i < boundingSpheres.length; i++) {
              const boundingSphere = boundingSpheres[i];
              unionBoundingSphere = Cesium.BoundingSphere.union(
                unionBoundingSphere,
                boundingSphere,
                scratchBoundingSphere
              );
            }
          }
        }
        if (this._polygonOutline?.ready) {
          let boundingSpheres;
          if (this._polygonOutline instanceof Cesium.GroundPolylinePrimitive) {
            const primitive = this._polygonOutline._primitive;
            boundingSpheres = primitive._boundingSpheres;
          } else {
            boundingSpheres = this._polygonOutline._boundingSpheres;
          }
          if (boundingSpheres.length >= 1) {
            let startIndex = 0;
            if (!unionBoundingSphere) {
              unionBoundingSphere = boundingSpheres[0];
              startIndex = 1;
            }
            for (let i = startIndex; i < boundingSpheres.length; i++) {
              const boundingSphere = boundingSpheres[i];
              unionBoundingSphere = Cesium.BoundingSphere.union(
                unionBoundingSphere,
                boundingSphere,
                scratchBoundingSphere
              );
            }
          }
        }
        if (unionBoundingSphere) {
          this._viewer.camera.flyToBoundingSphere(unionBoundingSphere, options);
        }
        removeListener();
      });
    }
  }

  /**
   * 处理DataSource
   */
  _resolveDataSource() {
    const entities = this._dataSource.entities.values;

    const labelItems = [];

    this._resolvePoint(entities, labelItems);

    this._resolvePolyline(entities, labelItems);

    this._resolvePolygon(entities, labelItems);

    if (labelItems.length > 0) {
      this._labelCollection = new Cesium.LabelCollection({
        blendOption: Cesium.BlendOption.OPAQUE,
      });
      for (let i = 0; i < labelItems.length; i++) {
        const labItem = labelItems[i];
        this._labelCollection.add(labItem);
      }
      this._collection.add(this._labelCollection);
    }

    this._dataSource = undefined;
  }

  /**
   * 获取Label
   * @param {Cesium.Entity} entity
   * @returns {string?}
   */
  _getLabel(entity) {
    return undefined;
  }

  /**
   * 处理Point
   * @param {Cesium.Entity[]} entities
   * @param {object[]} labelItems
   */
  _resolvePoint(entities, labelItems) {
    const pointItems = [];
    for (let i = 0; i < entities.length; i++) {
      const entity = entities[i];
      if (entity.billboard) {
        let position;
        if (entity.position) {
          position = entity.position.getValue(Cesium.JulianDate.now());
        }

        let color = Cesium.Color.WHITE;
        if (entity.billboard.color) {
          color = entity.billboard.color.getValue(Cesium.JulianDate.now());
        }
        pointItems.push({
          position,
          pixelSize: 10,
          color: color,
          outlineColor: Cesium.Color.BLACK,
          outlineWidth: 1,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
        });

        const text = this._getLabel(entity);
        if (text) {
          labelItems.push({
            position: position,
            text: text,
            ...defaultLabelStyle,
            horizontalOrigin: Cesium.HorizontalOrigin.LEFT,
            pixelOffset: new Cesium.Cartesian2(10, 0),
          });
        }
      }
    }
    if (pointItems.length > 0) {
      this._pointCollection = new Cesium.PointPrimitiveCollection();
      for (let i = 0; i < pointItems.length; i++) {
        const pointItem = pointItems[i];
        this._pointCollection.add(pointItem);
      }
      this._collection.add(this._pointCollection);
    }
  }

  /**
   * 处理Polyline
   * @param {Cesium.Entity[]} entities
   * @param {object[]} labelItems
   */
  _resolvePolyline(entities, labelItems) {
    const polylineInstances = [];
    for (let i = 0; i < entities.length; i++) {
      const entity = entities[i];
      if (entity.polyline) {
        let positions = [];
        if (entity.polyline.positions) {
          positions = entity.polyline.positions.getValue(Cesium.JulianDate.now());
          const newPositions = [];
          for (let ii = 0; ii < positions.length; ii++) {
            const position = positions[ii];
            const geographic = coordTransform.fromWorldCartesian.toWGS84(position);
            geographic.alt = 0;
            const newPosition = coordTransform.fromWGS84.toWorldCartesian(geographic);
            newPositions.push(newPosition);
          }
          positions = newPositions;
        }

        let color = Cesium.Color.WHITE;
        if (entity.polyline.material instanceof Cesium.ColorMaterialProperty) {
          color = entity.polyline.material.color.getValue(Cesium.JulianDate.now());
        }

        let width = 3;
        if (entity.polyline.width) {
          width = entity.polyline.width.getValue(Cesium.JulianDate.now());
        }
        const polylineGeometry = new Cesium.GroundPolylineGeometry({
          positions: positions,
          width: width,
        });

        const polylineInstance = new Cesium.GeometryInstance({
          id: `polyline-${entity.id}`,
          geometry: polylineGeometry,
          attributes: {
            color: Cesium.ColorGeometryInstanceAttribute.fromColor(color),
            // depthFailColor: Cesium.ColorGeometryInstanceAttribute.fromColor(color),
          },
        });
        polylineInstances.push(polylineInstance);

        const text = this._getLabel(entity);
        if (text) {
          let textPosition;
          if (positions.length === 2) {
            textPosition = Cesium.Cartesian3.midpoint(
              positions[0],
              positions[1],
              new Cesium.Cartesian3()
            );
          } else {
            textPosition = positions[Math.floor(positions.length / 2)];
          }

          labelItems.push({
            position: textPosition,
            text: text,
            ...defaultLabelStyle,
          });
        }
      }
    }
    if (polylineInstances.length > 0) {
      const translucent = true;
      this._polyline = new Cesium.GroundPolylinePrimitive({
        geometryInstances: polylineInstances,
        appearance: new Cesium.PolylineColorAppearance({ translucent }),
        classificationType: Cesium.ClassificationType.TERRAIN,
      });
      this._collection.add(this._polyline);
    }
  }

  /**
   * 处理Polygon
   * @param {Cesium.Entity[]} entities
   * @param {object[]} labelItems
   */
  _resolvePolygon(entities, labelItems) {
    const polygonInstances = [];
    const groundPolygonInstances = [];
    const polygonOutlineInstances = [];
    const groundPolygonOutlineInstances = [];
    for (let i = 0; i < entities.length; i++) {
      const entity = entities[i];
      if (entity.polygon) {
        let height;
        const extendedData = entity.kml?.extendedData;
        if (extendedData && extendedData.height) {
          const heightValue = Number(extendedData.height.value);
          if (!isNaN(heightValue)) {
            height = heightValue;
          }
        }

        let hierarchy = undefined;
        if (entity.polygon.hierarchy) {
          hierarchy = entity.polygon.hierarchy.getValue(Cesium.JulianDate.now());
          this._recursionResolveHierarchy(hierarchy, height);
        }

        let fill = true;
        if (Cesium.defined(this._polygonStyle?.fill)) {
          fill = this._polygonStyle.fill;
        } else if (entity.polygon.fill) {
          fill = entity.polygon.fill.getValue(Cesium.JulianDate.now());
        }
        if (fill) {
          let color = Cesium.Color.WHITE;
          if (Cesium.defined(this._polygonStyle?.color)) {
            color = this._polygonStyle.color;
          } else if (entity.polygon.material instanceof Cesium.ColorMaterialProperty) {
            color = entity.polygon.material.color.getValue(Cesium.JulianDate.now());
          }

          if (Cesium.defined(height) && height !== 0) {
            const polygonInstance = new Cesium.GeometryInstance({
              id: `polygon-${entity.id}`,
              geometry: new Cesium.PolygonGeometry({
                polygonHierarchy: hierarchy ?? new Cesium.PolygonHierarchy([]),
                height: this._polygonStyle?.isExtrudedHeight ? 0 : height,
                extrudedHeight: this._polygonStyle?.isExtrudedHeight ? height : undefined,
                closeBottom: this._polygonStyle?.isExtrudedHeight ? false : true,
              }),
              attributes: {
                color: Cesium.ColorGeometryInstanceAttribute.fromColor(color),
                // depthFailColor: Cesium.ColorGeometryInstanceAttribute.fromColor(color),
              },
            });
            polygonInstances.push(polygonInstance);
          } else {
            const polygonInstance = new Cesium.GeometryInstance({
              id: `groundPolygon-${entity.id}`,
              geometry: new Cesium.PolygonGeometry({
                polygonHierarchy: hierarchy ?? new Cesium.PolygonHierarchy([]),
              }),
              attributes: {
                color: Cesium.ColorGeometryInstanceAttribute.fromColor(color),
                // depthFailColor: Cesium.ColorGeometryInstanceAttribute.fromColor(color),
              },
            });
            groundPolygonInstances.push(polygonInstance);
          }
        }

        let outline = true;
        if (Cesium.defined(this._polygonStyle?.outline)) {
          outline = this._polygonStyle.outline;
        } else if (entity.polygon.outline) {
          outline = entity.polygon.outline.getValue(Cesium.JulianDate.now());
        }
        if (outline) {
          let outlineColor = Cesium.Color.BLACK;
          if (Cesium.defined(this._polygonStyle?.outlineColor)) {
            outlineColor = this._polygonStyle.outlineColor;
          } else if (entity.polygon.outlineColor) {
            outlineColor = entity.polygon.outlineColor.getValue(Cesium.JulianDate.now());
          }

          let outlineWidth = 3;
          if (Cesium.defined(this._polygonStyle?.outlineWidth)) {
            outlineWidth = this._polygonStyle.outlineWidth;
          } else if (entity.polygon.outlineWidth) {
            outlineWidth = entity.polygon.outlineWidth.getValue(Cesium.JulianDate.now());
          }

          if (hierarchy) {
            const positionsArray = [];
            this._recursionGetHierarchyPositions(hierarchy, positionsArray);
            for (let ii = 0; ii < positionsArray.length; ii++) {
              const positions = positionsArray[ii];

              if (Cesium.defined(height) && height !== 0) {
                const outlineInstance = new Cesium.GeometryInstance({
                  id: `polygonOutline-${entity.id}`,
                  geometry: new Cesium.PolylineGeometry({
                    positions: positions,
                    width: outlineWidth,
                  }),
                  attributes: {
                    color: Cesium.ColorGeometryInstanceAttribute.fromColor(outlineColor),
                    // depthFailColor: Cesium.ColorGeometryInstanceAttribute.fromColor(outlineColor),
                  },
                });
                polygonOutlineInstances.push(outlineInstance);

                // 拉伸面的时候 创建侧面边框
                if (this._polygonStyle?.isExtrudedHeight) {
                  const topPositions = [...positions];
                  const bottomPositions = positions.map((position) => {
                    const cartographic = Cesium.Cartographic.fromCartesian(position);
                    return Cesium.Cartesian3.fromRadians(
                      cartographic.longitude,
                      cartographic.latitude,
                      0
                    );
                  });

                  for (let i = 0; i < topPositions.length; i++) {
                    const sidePosition = [topPositions[i], bottomPositions[i]];
                    const sideOutlineInstance = new Cesium.GeometryInstance({
                      id: `polygonSideOutline-${entity.id}-${i}`,
                      geometry: new Cesium.PolylineGeometry({
                        positions: sidePosition,
                        width: outlineWidth,
                      }),
                      attributes: {
                        color: Cesium.ColorGeometryInstanceAttribute.fromColor(outlineColor),
                        // depthFailColor: Cesium.ColorGeometryInstanceAttribute.fromColor(outlineColor),
                      },
                    });
                    polygonOutlineInstances.push(sideOutlineInstance);
                  }
                }
              } else {
                const outlineInstance = new Cesium.GeometryInstance({
                  id: `groundPolygonOutline-${entity.id}`,
                  geometry: new Cesium.GroundPolylineGeometry({
                    positions: positions,
                    width: outlineWidth,
                  }),
                  attributes: {
                    color: Cesium.ColorGeometryInstanceAttribute.fromColor(outlineColor),
                    // depthFailColor: Cesium.ColorGeometryInstanceAttribute.fromColor(outlineColor),
                  },
                });
                groundPolygonOutlineInstances.push(outlineInstance);
              }
            }
          }
        }

        if (hierarchy) {
          const positions = hierarchy.positions;
          const text = this._getLabel(entity);
          if (text) {
            let textPosition = positions.length > 0 ? geoMath.calcCenter(positions) : undefined;
            if (Cesium.defined(height) && height !== 0) {
              const textGeographic = coordTransform.fromWorldCartesian.toWGS84(textPosition);
              textGeographic.alt = height + 1;
              textPosition = coordTransform.fromWGS84.toWorldCartesian(textGeographic);
            }

            labelItems.push({
              position: textPosition,
              text: text,
              ...defaultLabelStyle,
            });
          }
        }
      }
    }

    const translucent = true;
    if (polygonInstances.length > 0) {
      this._polygon = new Cesium.Primitive({
        geometryInstances: polygonInstances,
        appearance: new Cesium.PerInstanceColorAppearance({ flat: true, translucent }),
        // depthFailAppearance: new Cesium.PerInstanceColorAppearance({ flat: true, translucent }),
      });

      this._collection.add(this._polygon);
    }
    if (polygonOutlineInstances.length > 0) {
      this._polygonOutline = new Cesium.Primitive({
        geometryInstances: polygonOutlineInstances,
        appearance: new Cesium.PolylineColorAppearance({ translucent }),
        // depthFailAppearance: new Cesium.PolylineColorAppearance({ translucent }),
      });
      this._collection.add(this._polygonOutline);
    }

    if (groundPolygonInstances.length > 0) {
      let appearance;
      if (this._polygonStyle?.useFlowWaterMaterial) {
        appearance = new Cesium.EllipsoidSurfaceAppearance({
          material: new Cesium.Material({
            fabric: {
              type: 'FlowWater',
              uniforms: {
                normalMap: Cesium.buildModuleUrl('Assets/Textures/waterNormals.jpg'), // 法线贴图
                baseWaterColor: Cesium.Color.fromCssColorString('#334c99'), // 水流主体颜色
                blendColor: Cesium.Color.fromCssColorString('#00ffb3'), // 水底混入颜色
                frequency: 100.0, // 水波频率，数值越大水的波纹越多
                animationSpeed: 0.01, // 动画速率
                amplitude: 2.0, // 和切线空间的法线相关，值越大水波幅度越大
                specularIntensity: 0.5, // 高光强度
                fadeFactor: 1.0,
                flowdirection: 5.0, // 流向，可以设置0、1、2、3、4、5
              },
            },
          }),
          renderState: {
            cull: false,
          },
          aboveGround: false,
          translucent: true,
        });
      } else {
        appearance = new Cesium.PerInstanceColorAppearance({ flat: true, translucent });
      }

      this._polygon = new Cesium.GroundPrimitive({
        geometryInstances: groundPolygonInstances,
        appearance: appearance,
        classificationType: Cesium.ClassificationType.TERRAIN,
      });
      this._collection.add(this._polygon);
    }
    if (groundPolygonOutlineInstances.length > 0) {
      this._polygonOutline = new Cesium.GroundPolylinePrimitive({
        geometryInstances: groundPolygonOutlineInstances,
        appearance: new Cesium.PolylineColorAppearance({ translucent }),
        classificationType: Cesium.ClassificationType.TERRAIN,
      });
      this._collection.add(this._polygonOutline);
    }
  }

  /**
   * 递归处理Hierarchy的高度
   * @param {Cesium.PolygonHierarchy} hierarchy
   * @param {number} height
   */
  _recursionResolveHierarchy(hierarchy, height) {
    const positions = hierarchy.positions;
    const newPositions = [];
    for (let i = 0; i < positions.length; i++) {
      const position = positions[i];
      const geographic = coordTransform.fromWorldCartesian.toWGS84(position);
      geographic.alt = height ?? 0;
      const newPosition = coordTransform.fromWGS84.toWorldCartesian(geographic);
      newPositions.push(newPosition);
    }
    hierarchy.positions = newPositions;

    const holes = hierarchy.holes;
    for (let i = 0; i < holes.length; i++) {
      const hole = holes[i];
      this._recursionResolveHierarchy(hole, height);
    }
  }

  /**
   * 递归获取Hierarchy的所有positions
   * @param {Cesium.PolygonHierarchy} hierarchy
   * @param {Cesium.Cartesian3[][]} positionsArray
   */
  _recursionGetHierarchyPositions(hierarchy, positionsArray) {
    positionsArray.push(hierarchy.positions);

    const holes = hierarchy.holes;
    for (let i = 0; i < holes.length; i++) {
      const hole = holes[i];
      this._recursionGetHierarchyPositions(hole, positionsArray);
    }
  }

  /**
   * 销毁
   */
  destroy() {
    this.removeFromViewer(true);
    this._pointCollection = null;
    this._polyline = null;
    this._polygon = null;
    this._polygonOutline = null;
    this._labelCollection = null;
    this._collection = null;
  }

  /**
   * 从kmljson文件创建
   * @param {string} url
   * @param {object} options
   * @param {string} options.id 唯一标识
   * @param {string} options.name 名称
   * @param {PolygonStyle?} options.polygonStyle 多边形样式
   * @returns {VectorPrimitive}
   */
  static async fromKml(url, options) {
    const dataSource = await Cesium.KmlDataSource.load(url);

    options = options ?? {};
    const feature = new this({
      id: options.id,
      name: options.name,
      dataSource,
      polygonStyle: options.polygonStyle,
    });
    return feature;
  }
}

export default VectorPrimitive;
